import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface LogPhoneCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId?: string;
  customerId?: string;
  onSaved?: () => void;
  defaultPhone?: string;
  existingLogId?: string | null;
  defaultValues?: {
    direction?: "outbound" | "inbound";
    phoneNumber?: string;
    contactName?: string;
  };
}

export function LogPhoneCallDialog({
  open,
  onOpenChange,
  orderId,
  customerId,
  onSaved,
  defaultPhone = "",
  existingLogId,
  defaultValues,
}: LogPhoneCallDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<"outbound" | "inbound">("outbound");
  const [phoneNumber, setPhoneNumber] = useState(defaultPhone);
  const [subject, setSubject] = useState("");
  const [summary, setSummary] = useState("");
  const [duration, setDuration] = useState<number | undefined>();

  // Initialize form with defaultValues when dialog opens
  useEffect(() => {
    if (open && defaultValues) {
      if (defaultValues.direction) setDirection(defaultValues.direction);
      if (defaultValues.phoneNumber) setPhoneNumber(defaultValues.phoneNumber);
    } else if (open) {
      setPhoneNumber(defaultPhone);
    }
  }, [open, defaultValues, defaultPhone]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!summary.trim()) {
        throw new Error("Yhteenveto on pakollinen");
      }

      if (existingLogId) {
        // Update existing log entry
        const { error } = await supabase
          .from("communication_logs")
          .update({
            summary: summary.trim(),
            subject: subject.trim() || null,
            status: "completed",
            metadata: {
              duration_minutes: duration,
              contact_name: defaultValues?.contactName,
              completed_at: new Date().toISOString(),
            },
          })
          .eq("id", existingLogId);

        if (error) throw error;
      } else {
        // Create new log entry
        const { error } = await supabase.from("communication_logs").insert({
          order_id: orderId || null,
          customer_id: customerId || null,
          communication_type: "phone",
          direction,
          subject: subject.trim() || null,
          summary: summary.trim(),
          from_address: direction === "inbound" ? phoneNumber : null,
          to_address: direction === "outbound" ? phoneNumber : null,
          status: "completed",
          metadata: duration ? { duration_minutes: duration } : {},
          created_by: user?.id || null,
        });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(existingLogId ? "Puhelun tiedot päivitetty" : "Puhelu kirjattu");
      queryClient.invalidateQueries({ queryKey: ["communication-logs"] });
      resetForm();
      onOpenChange(false);
      onSaved?.();
    },
    onError: (error) => {
      toast.error("Virhe", {
        description: error instanceof Error ? error.message : "Puhelun kirjaus epäonnistui",
      });
    },
  });

  const resetForm = () => {
    setDirection("outbound");
    setPhoneNumber(defaultPhone);
    setSubject("");
    setSummary("");
    setDuration(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{existingLogId ? "Lisää puhelun muistiinpanot" : "Kirjaa puhelu"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!existingLogId && (
            <div>
              <Label>Puhelun suunta *</Label>
              <RadioGroup
                value={direction}
                onValueChange={(v) => setDirection(v as "outbound" | "inbound")}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="outbound" id="outbound" />
                  <Label htmlFor="outbound" className="font-normal cursor-pointer">
                    Soitettiin asiakkaalle
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="inbound" id="inbound" />
                  <Label htmlFor="inbound" className="font-normal cursor-pointer">
                    Asiakas soitti
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {!existingLogId && (
            <div>
              <Label htmlFor="phone">Puhelinnumero</Label>
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="esim. +358401234567"
              />
            </div>
          )}

          {existingLogId && defaultValues?.contactName && (
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Asiakas</p>
              <p className="font-medium">{defaultValues.contactName}</p>
              {phoneNumber && <p className="text-sm text-muted-foreground">{phoneNumber}</p>}
            </div>
          )}

          <div>
            <Label htmlFor="subject">Aihe</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="esim. Asennusaikataulu"
            />
          </div>

          <div>
            <Label htmlFor="summary">Yhteenveto *</Label>
            <Textarea
              id="summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Mitä puhelussa sovittiin..."
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="duration">Kesto (min)</Label>
            <Input
              id="duration"
              type="number"
              min="1"
              value={duration || ""}
              onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="esim. 5"
              className="w-24"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Peruuta
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Tallennetaan..." : "Tallenna"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
